<template>
  <v-sheet :class="['appointment-card', { 'appointment-card--actions-only': actionsOnly }]" :outlined="!actionsOnly" rounded="lg">
    <template v-if="!actionsOnly">
      <div class="appointment-card__top">
        <div class="appointment-card__time">
          <v-icon small class="mr-1">mdi-clock-outline</v-icon>
          {{ timeRange }}
        </div>
        <v-chip x-small :color="statusMeta.color" :text-color="statusMeta.textColor">
          {{ statusMeta.label }}
        </v-chip>
      </div>

      <div class="appointment-card__service">{{ appointment.service_type_name }}</div>
      <div class="appointment-card__guest">
        {{ appointment.guest_first_name }} {{ appointment.guest_last_name }}
        <span v-if="appointment.guest_email" class="grey--text"> · {{ appointment.guest_email }}</span>
      </div>
      <div v-if="appointment.telegram_username" class="appointment-card__telegram grey--text text--darken-1">
        Telegram: {{ appointment.telegram_username }}
      </div>
    </template>
    <div v-if="appointment.conference_url" class="appointment-card__conference">
      <a :href="appointment.conference_url" target="_blank" rel="noopener noreferrer">{{ appointment.conference_url }}</a>
    </div>

    <!-- Ожидающая заявка, время которой прошло: подтвердить нельзя, только отклонить. -->
    <div v-if="appointment.status === 'pending' && isPast" class="appointment-card__actions">
      <div class="appointment-card__expired mb-2">
        <v-icon small color="#8a8f9c" class="mr-1">mdi-clock-alert-outline</v-icon>
        Время записи прошло — подтвердить уже нельзя.
      </div>
      <v-btn small outlined color="#EC2E2E" class="mb-2" :loading="loading" @click="$emit('decline', appointment.id)">
        Отклонить
      </v-btn>
    </div>

    <div v-else-if="appointment.status === 'pending'" class="appointment-card__actions">
      <!-- Постоянная ссылка эксперта подставляется сама; «Изменить» — заменить её для этой записи. -->
      <div v-if="defaultConferenceUrl && !editingUrl" class="appointment-card__default-url mb-2">
        <v-icon small color="grey" class="mr-1 flex-shrink-0">mdi-video-outline</v-icon>
        <span class="grey--text text-truncate flex-grow-1" :title="defaultConferenceUrl">{{ defaultConferenceUrl }}</span>
        <v-btn x-small text color="#483CB9" class="text-none flex-shrink-0" @click="startEditUrl">Изменить</v-btn>
      </div>
      <v-text-field
        v-else
        v-model="conferenceUrl"
        :placeholder="defaultConferenceUrl ? 'Ссылка на конференцию' : 'Ссылка на конференцию (необязательно)'"
        outlined
        dense
        hide-details
        :autofocus="editingUrl"
        :append-icon="defaultConferenceUrl ? 'mdi-close' : ''"
        class="mb-2"
        @click:append="resetUrl"
      />
      <div class="d-flex flex-wrap">
        <v-btn small color="#009E60" dark class="mr-2 mb-2" :loading="loading" @click="$emit('accept', { id: appointment.id, conference_url: effectiveConferenceUrl })">
          Подтвердить
        </v-btn>
        <v-btn small outlined color="#EC2E2E" class="mr-2 mb-2" :loading="loading" @click="$emit('decline', appointment.id)">
          Отклонить
        </v-btn>
        <v-btn
          small
          outlined
          color="#483CB9"
          class="mb-2"
          :href="googleCalendarUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <v-icon small left>mdi-google</v-icon>
          В Google Календарь
        </v-btn>
      </div>
    </div>

    <div v-else-if="appointment.status === 'confirmed'" class="appointment-card__actions d-flex flex-column align-start">
      <v-btn small outlined color="#483CB9" class="mb-2" :loading="loading" @click="$emit('edit-conference-link', appointment)">
        {{ appointment.conference_url ? 'Изменить ссылку' : 'Добавить ссылку' }}
      </v-btn>
      <v-btn
        v-if="googleCalendarUrl"
        small
        outlined
        color="#483CB9"
        :href="googleCalendarUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <v-icon small left>mdi-google</v-icon>
        В Google Календарь
      </v-btn>
    </div>
  </v-sheet>
</template>

<script>
import { utcToLocalTime } from '@/common/calendarTime';
import { buildGoogleCalendarUrl } from '@/common/googleCalendar';
import { isPastAppointment, statusMeta } from './appointmentHelpers';

export default {
  name: 'AppointmentCard',
  props: {
    appointment: { type: Object, required: true },
    // Постоянная ссылка на конференцию эксперта записи (permanent_conference_url).
    defaultConferenceUrl: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    // Только ссылка на конференцию и действия — данные записи показаны снаружи (панель «Записи»).
    actionsOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      conferenceUrl: this.appointment.conference_url || '',
      // Ожидающая запись: постоянная ссылка заменена вручную (или у записи уже своя ссылка).
      editingUrl: Boolean(this.appointment.conference_url),
    };
  },
  watch: {
    // Компонент переиспользуется при перезагрузке списка (тот же :key),
    // поэтому синхронизируем локальное поле с обновлённым значением из стора.
    'appointment.conference_url': function onConferenceUrlChange(value) {
      this.conferenceUrl = value || '';
    },
  },
  computed: {
    // Ссылка, которая уйдёт при подтверждении: введённая, а если поле пустое — постоянная.
    effectiveConferenceUrl() {
      return this.conferenceUrl.trim() || this.defaultConferenceUrl;
    },
    timeRange() {
      return `${utcToLocalTime(this.appointment.scheduled_starts_at)} – ${utcToLocalTime(this.appointment.scheduled_ends_at)}`;
    },
    guestName() {
      return [this.appointment.guest_first_name, this.appointment.guest_last_name].filter(Boolean).join(' ');
    },
    googleCalendarUrl() {
      const {
        service_type_name: serviceName, guest_email: email, telegram_username: telegram,
      } = this.appointment;
      // До подтверждения в событие идёт ссылка, которая будет отправлена при подтверждении.
      const conferenceUrl = this.appointment.status === 'pending'
        ? this.effectiveConferenceUrl
        : this.appointment.conference_url;
      const details = [
        this.guestName && `Клиент: ${this.guestName}`,
        email && `Email: ${email}`,
        telegram && `Telegram: ${telegram}`,
        conferenceUrl && `Ссылка на конференцию: ${conferenceUrl}`,
      ].filter(Boolean).join('\n');

      return buildGoogleCalendarUrl({
        title: [serviceName, this.guestName].filter(Boolean).join(' — '),
        startsAt: this.appointment.scheduled_starts_at,
        endsAt: this.appointment.scheduled_ends_at,
        details,
        location: conferenceUrl,
      });
    },
    statusMeta() {
      return statusMeta(this.appointment.status);
    },
    isPast() {
      return isPastAppointment(this.appointment);
    },
  },
  methods: {
    startEditUrl() {
      this.conferenceUrl = this.defaultConferenceUrl;
      this.editingUrl = true;
    },
    // Сброс к постоянной ссылке эксперта.
    resetUrl() {
      this.conferenceUrl = '';
      this.editingUrl = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.appointment-card {
  padding: 12px;
  margin-bottom: 12px;

  &__expired {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #8a8f9c;
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__time {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 500;
  }

  &__service {
    font-size: 14px;
    font-weight: 600;
  }

  &__guest {
    font-size: 12px;
    margin: 2px 0;
  }

  &__telegram, &__conference {
    font-size: 12px;
    margin-bottom: 4px;
  }

  &__default-url {
    display: flex;
    align-items: center;
    min-width: 0;
    font-size: 12px;
    padding: 6px 4px 6px 8px;
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &__actions {
    margin-top: 8px;
  }

  &--actions-only {
    padding: 0;
  }
}
</style>
